import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { FormSelect } from '@app/shared/components/form-controls/form-select/FormSelect';
import { WATERFALL_SECTION_OPTIONS } from '@app/shared/constants/dropdown-values/waterfall-sections';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { RouteConstants } from '../../RouteConstants';
import styles from '@app/modules/projects/outputs/outputs-table.module.scss';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { selectSortedCasesStructure } from '@app/core/store/project-slice-selectors';
import { Pwerm2WaterfallTableHeader } from './Pwerm2WaterfallTableHeader';
import { Pwerm2WaterfallExitMultiplesPartialTable } from './exit-multiples/Pwerm2WaterfallExitMultiplesPartialTable';
import TableScreenshotAction from '@app/shared/components/table/TableScreenshotAction';
import { removeFileIllegalCharacters } from '@app/shared/helpers/remove-file-illegal-characters';
import { FC, useEffect, useMemo, useRef } from 'react';
import { Pwerm2WaterfallEnterpriseValuePartialTable } from './enterprise-value/Pwerm2WaterfallEnterpriseValuePartialTable';
import { Pwerm2WaterfallNetExitProceedsPartialTable } from './net-exit-proceeds/Pwerm2WaterfallNetExitProceedsPartialTable';
import { Pwerm2WaterfallTotalEquityProceedsPartialTable } from './total-equity-proceeds/Pwerm2WaterfallTotalEquityProceedsPartialTable';
import { Pwerm2WaterfallShareholderDebtDistributionsPartialTable } from './shareholder-debt-distributions/Pwerm2WaterfallShareholderDebtDistributionsPartialTable';
import { Pwerm2WaterfallOrdinaryEquityPartialTable } from './ordinary-equity/Pwerm2WaterfallOrdinaryEquityPartialTable';
import { Pwerm2WaterfallValuationPartialTable } from './valuation/Pwerm2WaterfallValuationPartialTable';
import { Pwerm2WaterfallCrossCheckPartialTable } from './cross-check/Pwerm2WaterfallCrossCheckPartialTable';
import { Pwerm2WaterfallSponsorReturnPartialTable } from './sponsor-return/PartialTable';
import { useLocale } from '@app/core/hooks/useLocale';
import { useDownloadExcel } from '@app/shared/components/excel-exporter/ExcelExporter';
import { dateFormatter } from '@app/shared/formatters';
import { WATERFALL_SECTION_ERF_OPTIONS } from '@app/shared/constants/dropdown-values/waterfall-sections-erf';
import { Pwerm2WaterfallInterimCashFlowMovementByInstrument } from './other-cashflow-movement/Pwerm2WaterfallInterimCashFlowMovementByInstrument';
import { hasInterimTransactions } from '@app/core/store/pwerm2-calculation-slice-selectors';

interface Pwerm2WaterfallTableProps {
  shouldRenderInnerContent?: boolean;
  excelExportEnabled?: boolean;
  removeStickyColumns?: boolean;
  getRef?: (ref: HTMLTableElement) => void;
}

export const Pwerm2WaterfallTable: FC<Pwerm2WaterfallTableProps> = ({
  shouldRenderInnerContent = true,
  excelExportEnabled = false,
  removeStickyColumns = false,
  getRef,
}) => {
  const project = useAppSelector((state) => state.project.projectDraft);
  const buildStructures = useAppSelector((state) => state.capitalStructure.values.buildStructures);
  const capitalStructureId = project.pwermInput.cases[0].capitalStructureId;
  const isErfProject = buildStructures?.[capitalStructureId]?.isErf;
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();
  const { generatePath } = usePwcNavigate();
  const tableRef = useRef<HTMLTableElement>(null);
  const casesStructure = useAppSelector(selectSortedCasesStructure);
  const { isPwermValid, projectDraft } = useAppSelector((state) => state.project);
  const hasAnyInterimTransactions = useMemo(() => hasInterimTransactions(results), [results]);

  useEffect(() => {
    if (tableRef.current && getRef) {
      getRef(tableRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formMethods = useForm({
    ...formConfigBase,
    defaultValues: {
      scrollToSection: null,
    },
  });

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: `${removeFileIllegalCharacters(projectDraft?.name)}_Waterfall2_${dateFormatter(
      new Date().toString(),
      {
        dateStyle: 'short',
      }
    )}`,
    sheet: 'Waterfall PWERM2',
  });

  useEffect(() => {
    if (shouldRenderInnerContent && excelExportEnabled) {
      onDownload();
    }
    // effect tracks only one dependency
  }, [shouldRenderInnerContent]); // eslint-disable-line react-hooks/exhaustive-deps

  const { handleSubmit, getValues } = formMethods;

  const submitData = () => {
    if (getValues() && getValues().scrollToSection) {
      const scrollToSection = document.getElementById(
        (getValues().scrollToSection ?? '').toString()
      );
      scrollToSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const columnsCount =
    casesStructure.length + casesStructure.flatMap((caseItem) => caseItem.forecasts).length + 1;

  // this table is rendered in the header for the Excel export, so we don't want to redirect
  // if this component is the header instance
  if (!isPwermValid && !excelExportEnabled) {
    const newPath = generatePath(`../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  const shouldShowWaterfallSectionErfOptions = isErfProject || hasAnyInterimTransactions;

  return (
    <div className="main-container-padding-remove">
      <div className="main-container__additional-actions">
        <TableScreenshotAction
          tableRef={tableRef}
          testIdString="save-waterfall-image"
          filePrefix={`Waterfall_PWERM_${removeFileIllegalCharacters(projectDraft?.name)}_`}
        />
        <FormProvider {...formMethods}>
          <form
            onChange={handleSubmit(submitData, submitData)}
            className="main-container__section-selector">
            <FormSelect
              ariaLabel={l('_SelectorAriaLabel', {
                label: l('_WaterfallSection'),
              })}
              name="scrollToSection"
              required
              options={
                shouldShowWaterfallSectionErfOptions
                  ? WATERFALL_SECTION_ERF_OPTIONS
                  : WATERFALL_SECTION_OPTIONS
              }
              isGapless
              ignoreReadOnly
            />
          </form>
        </FormProvider>
      </div>
      <table
        ref={tableRef}
        className={`table-primary table-primary--zebra table-primary--framed table-primary--title-column-nowrap table-primary--waterfall ${
          removeStickyColumns ? '' : 'table-primary--sticky-first-column'
        }`}
        data-testid="waterfall-table">
        {shouldRenderInnerContent && (
          <>
            <colgroup>
              <col className={styles['labels-col']} />
              <col style={{ minWidth: '80px' }} />
              {[...Array(columnsCount - 2)].map((_, index) => (
                <col key={`col-${index}`} />
              ))}
            </colgroup>
            <Pwerm2WaterfallTableHeader />
            <Pwerm2WaterfallExitMultiplesPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallEnterpriseValuePartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallNetExitProceedsPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallTotalEquityProceedsPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallShareholderDebtDistributionsPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallOrdinaryEquityPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallInterimCashFlowMovementByInstrument columnsCount={columnsCount} />
            <Pwerm2WaterfallValuationPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallSponsorReturnPartialTable columnsCount={columnsCount} />
            <Pwerm2WaterfallCrossCheckPartialTable columnsCount={columnsCount} />
          </>
        )}
      </table>
    </div>
  );
};
